const formatFoundApplication = (data) => {
  return {
    applicationId: data?.applicationId,
    applicationNumber: data?.applicationNumber,
    firstName: data?.firstName ?? '-',
    lastName: data?.lastName ?? '-',
    dob: data?.dob ?? '',
    email: data?.email ?? '',
    phone: data?.phone ?? '',
    nationalInsuranceNumber: data?.nationalInsuranceNumber ?? '',
    currentAddress: {
      street: data?.currentAddress?.street ?? '',
      state: data?.currentAddress?.state ?? '',
      postalCode: data?.currentAddress?.postalCode ?? '',
      country: data?.currentAddress?.country ?? '',
      city: data?.currentAddress?.city ?? '',
      countryCode: data?.currentAddress?.countryCode ?? ''
    },
    cardData: {
      cardBrand: data?.cardData?.cardBrand ?? '-',
      expireMonth: data?.cardData?.expireMonth ?? 0,
      expireYear: data?.cardData?.expireYear ?? '-',
      last4Digits: data?.cardData?.last4Digits ?? '-'
    },
    paymentMethod: data?.paymentMethod ?? '',
    customerId: data?.customerId ?? '',
    isPartnerXPClient: false,
    isShowApplicationDetails: data?.isShowApplicationDetails ?? false
  };
};

// Latest Comments
const formatLatestComments = (data = []) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      if (item?.commentId) {
        result.push(formatLatestComment(item));
      }
    });
  }
  return result;
};

const formatLatestComment = (data) => {
  let body = '';
  if (data?.body) {
    body = data.body.replace('style="overflow-y: hidden;"', '');
  }
  return {
    commentId: data?.commentId ?? '',
    subject: data?.subject ?? '-',
    body: body ?? '-',
    toAddress: data?.toAddress ?? '-',
    fromAddress: data?.fromAddress ?? '-',
    createdDate: data?.createdDate ?? '',
    attachments: formatAttachments(data?.attachments ?? [])
  };
};
const formatAttachments = (data) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      if (item?.attachmentId) {
        result.push({
          attachmentId: item?.attachmentId ?? '',
          name: item?.name ?? '',
          isDownloadLoading: false
        });
      }
    });
  }
  return result;
};

// FAQ
const formatFAQs = (data = []) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      result.push(formatFAQ(item));
    });
  }
  return result;
};

const formatFAQ = (data) => {
  return {
    topic: data?.topic ?? '-',
    question: data?.question ?? '-',
    answer: data?.answer ?? '-',
    isOpen: false
  };
};

// Documents
const formatDocuments = (data = []) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      if (item?.documentId) {
        result.push(formatDocument(item));
      }
    });
  }
  return result;
};

const formatDocument = (data) => {
  return {
    documentId: data?.documentId,
    documentSize: formatFileSize(data?.documentSize ?? 0),
    documentTitle: data?.documentTitle ?? '-',
    documentType: data?.documentType ?? '',
    createdDate: data?.createdDate ?? ''
  };
};

const formatFileSize = (size) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Owerview Page
const formatOwerviewData = (data) => {
  return {
    pensionDate: data?.pensionDate ? formatDate(data?.pensionDate) : '-',
    pensionAge: data?.pensionAge ?? '-',
    timeRemaining: data?.pensionDate ? getTimeRemaining(data?.pensionDate) : '-',
    payFrom: data?.payFrom ?? '-',
    isPaid: data?.isPaid ?? false,
    taxYear: data?.taxYear ?? '-',
    taxYearAllocation: formatTaxYearAllocation(data?.taxYearAllocation ?? [])
  };
};
function getTimeRemaining(targetDateString) {
  const targetDate = new Date(targetDateString);
  const now = new Date();
  if (targetDate <= now) {
    return 'Target date has already passed or is now';
  }

  let years = targetDate.getFullYear() - now.getFullYear();
  let months = targetDate.getMonth() - now.getMonth();
  let days = targetDate.getDate() - now.getDate();
  let hours = targetDate.getHours() - now.getHours();
  let minutes = targetDate.getMinutes() - now.getMinutes();
  let seconds = targetDate.getSeconds() - now.getSeconds();

  if (seconds < 0) {
    seconds += 60;
    minutes--;
  }

  if (minutes < 0) {
    minutes += 60;
    hours--;
  }

  if (hours < 0) {
    hours += 24;
    days--;
  }

  if (days < 0) {
    const prevMonth = new Date(targetDate.getFullYear(), targetDate.getMonth(), 0);
    days += prevMonth.getDate();
    months--;
  }

  if (months < 0) {
    months += 12;
    years--;
  }

  const formatNumber = (num, digits = 2) => {
    return num.toString().padStart(digits, '0');
  };

  const resultParts = [];

  if (years > 0) {
    resultParts.push(`${years} Year${years !== 1 ? 's' : ''}`);
  }

  if (months > 0 || years > 0) {
    resultParts.push(`${formatNumber(months)} Month${months !== 1 ? 's' : ''}`);
  }

  resultParts.push(`${formatNumber(days)} Day${days !== 1 ? 's' : ''}`);
  // resultParts.push(`${formatNumber(hours)} Hour${hours !== 1 ? 's' : ''}`);
  // resultParts.push(`${formatNumber(minutes)} Minute${minutes !== 1 ? 's' : ''}`);
  // resultParts.push(`${formatNumber(seconds)} Second${seconds !== 1 ? 's' : ''}`);

  return resultParts.join(', ');
}
function formatDate(dateString) {
  // Split the input date string
  const [year, month, day] = dateString.split('-');

  // Array of month names
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  // Convert month number to name (subtract 1 for zero-based array index)
  const monthName = monthNames[parseInt(month) - 1];

  // Format the date with leading zero for day if needed
  // (though the input already has it, this makes it robust)
  const formattedDay = day.padStart(2, '0');

  return `${monthName} ${formattedDay}, ${year}`;
}

// Check Your Entitlement
const formatCheckYourEntitlementData = (data) => {
  return {
    entitlement: data?.entitlement ?? 0,
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    numberOfTickBoxes: data?.numberOfTickBoxes ?? 0,
    fullEntitlement: data?.fullEntitlement ?? 0,
    pension_age: data?.pension_age ?? '-',
    uk_years: data?.uk_years ?? 0,
    projection: data?.projection ?? 0,
    extraYears: data?.extraYears ?? 0,
    yearsToBuyLimit: data?.yearsToBuyLimit ?? 0,
    extraYearsOptions: formatExtraYearsOptions(data?.yearsToBuyLimit ?? 0),
    c3Cost: data?.c3Cost ?? 0,
    taxYearAllocation: formatTaxYearAllocation(data?.taxYearAllocation ?? [])
  };
};
const formatExtraYearsOptions = (limit) => {
  let result = [];
  for (let i = 1; i <= limit; i++) {
    result.push({
      label: i,
      value: i
    });
  }
  return result;
};

const formatTaxYearAllocation = (data) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      result.push({
        label: item?.label,
        isChecked: item?.isChecked ?? false
      });
    });
  }
  return result;
};

const formatRequestsCheck = (data) => {
  return {
    amountPaid: data?.amountPaid ?? 0,
    checkDate: data?.checkDate ?? '-',
    paymentReceived: data?.paymentReceived ?? '-'
  };
};

// Billing Page
const formatBillingData = (data) => {
  return {
    amount: data?.amount ?? 0,
    renewsDate: data?.renewsDate ?? '-',
    curencyCode: formatCurrencySymbol(data?.curencyCode ?? ''),
    customerId: data?.customerId ?? '',
    cardData: {
      cardBrand: data?.cardData?.cardBrand ?? '-',
      expireMonth: data?.cardData?.expireMonth ?? 0,
      expireYear: data?.cardData?.expireYear ?? '-',
      last4Digits: data?.cardData?.last4Digits ?? '-'
    },
    receiptData: formatReceiptData(data?.receiptData ?? [])
  };
};

const formatReceiptData = (data) => {
  let result = [];
  if (data?.length) {
    data.forEach((item) => {
      if (item?.paymentId) {
        result.push({
          paymentId: item?.paymentId,
          amount: item?.amount ?? 0,
          paymentDate: item?.paymentDate ?? '-',
          currencyCode: formatCurrencySymbol(item?.currencyCode ?? '')
        });
      }
    });
  }
  return result;
};

const formatCurrencySymbol = (currencyCode = '') => {
  let currencySymbol = '$';
  if (currencyCode === 'EUR') {
    currencySymbol = '€';
  } else if (currencyCode === 'CA') {
    currencySymbol = 'CA $';
  } else if (currencyCode === 'AU') {
    currencySymbol = 'AU $';
  } else if (currencyCode === 'NZ') {
    currencySymbol = 'NZ $';
  } else if (currencyCode === 'ZA') {
    currencySymbol = 'R';
  }
  return currencySymbol;
};

const formatFormApplication = (data) => {
  return {
    id: data?.id,
    firstName: data?.firstName ?? '-',
    lastName: data?.lastName ?? '-',
    title: data?.title ?? '-',
    email: data?.email ?? '-',
    phone: data?.phone ?? '-',
    nino: data?.nino ?? '',
    ukYears: data?.ukYears ?? 0,
    dateOfBirth: data?.dob ?? '',
    currentAddress: {
      street: data?.currentAddress?.street ?? '',
      state: data?.currentAddress?.state ?? '',
      postalCode: data?.currentAddress?.postalCode ?? '',
      country: data?.currentAddress?.country ?? '',
      city: data?.currentAddress?.city ?? '',
      countryCode: data?.currentAddress?.countryCode ?? ''
    },
    document: {
      base64: '',
      fileType: '',
      fileName: '',
      recordId: ''
    }
  };
};
export {
  formatFoundApplication,
  formatDocuments,
  formatDocument,
  formatFAQs,
  formatLatestComments,
  formatLatestComment,
  formatOwerviewData,
  formatCheckYourEntitlementData,
  formatRequestsCheck,
  formatBillingData,
  formatFormApplication
};
